import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Plus,
  Upload,
  Edit2,
  Trash2,
  Copy,
  X,
  Code,
  Search,
  Loader2,
  FileCode2,
  Check,
  Eye,
  FileText,
} from "lucide-react";
import api from "../utils/api";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { useToast } from "../context/ToastContext";

const SAMPLE_SKILL_MD = `---
name: "customer_support_skill"
description: "Handles customer inquiries, order refunds, and product returns."
---

# Customer Support Skill

## Role & Goal
You are a Customer Support Specialist. Your goal is to guide customers through return policies and refund inquiries accurately.

## Workflow Rules
1. Ask for Order ID and return reason.
2. Check 30-day return eligibility window.
3. Respond in clean markdown bullet points.`;

const Skills = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [skills, setSkills] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Create / Edit Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    instructions: "",
    is_active: true
  });

  // Code / Instructions Viewer Modal State
  const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);
  const [selectedSkillDetail, setSelectedSkillDetail] = useState(null);
  const [isCodeLoading, setIsCodeLoading] = useState(false);
  const [codeModalTab, setCodeModalTab] = useState("instructions"); // "instructions" | "json"
  const [isCopied, setIsCopied] = useState(false);

  // Sample Template Modal State
  const [isSampleModalOpen, setIsSampleModalOpen] = useState(false);

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // File Upload State
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch Skills
  const fetchSkills = useCallback(async () => {
    setIsLoading(true);
    try {
      if (activeAiAgentId) {
        api.setAiAgentId(activeAiAgentId);
      }
      const data = await api.get("/skills");
      setSkills(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching skills:", err);
      showToast("Failed to load skills for this agent", "error");
    } finally {
      setIsLoading(false);
    }
  }, [activeAiAgentId, showToast]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  // Open Create / Edit Modal
  const handleOpenModal = async (skill = null) => {
    if (skill) {
      setEditingSkill(skill);
      setIsModalLoading(true);
      setIsModalOpen(true);
      try {
        const detail = await api.get(`/skills/${skill.id}`);
        setFormData({
          name: detail.name || skill.name || "",
          description: detail.description || skill.description || "",
          instructions: detail.instructions || skill.instructions || "",
          is_active: detail.is_active !== undefined ? detail.is_active : (skill.is_active !== undefined ? skill.is_active : true)
        });
      } catch (err) {
        console.error("Failed to load skill details:", err);
        setFormData({
          name: skill.name || "",
          description: skill.description || "",
          instructions: skill.instructions || "",
          is_active: skill.is_active !== undefined ? skill.is_active : true
        });
      } finally {
        setIsModalLoading(false);
      }
    } else {
      setEditingSkill(null);
      setFormData({
        name: "",
        description: "",
        instructions: `# Skill Instructions\n\nProvide step-by-step instructions, rules, or references for the AI agent here.\n\nExample:\n- Always format responses using Markdown bullet points.\n- Double-check API parameters before executing queries.`,
        is_active: true
      });
      setIsModalOpen(true);
    }
  };

  // Open Code / Instructions Modal
  const handleOpenCodeModal = async (skill) => {
    setIsCodeModalOpen(true);
    setIsCodeLoading(true);
    setSelectedSkillDetail(skill);
    setCodeModalTab("instructions");
    setIsCopied(false);

    try {
      const detail = await api.get(`/skills/${skill.id}`);
      setSelectedSkillDetail(detail);
    } catch (error) {
      console.error("Error fetching skill detail:", error);
      showToast("Failed to fetch complete skill instructions", "error");
    } finally {
      setIsCodeLoading(false);
    }
  };

  // Open Delete Modal
  const handleOpenDeleteModal = (skill) => {
    setSkillToDelete(skill);
    setIsDeleteModalOpen(true);
  };

  // Submit Form (Create / Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showToast("Skill name is required", "error");
      return;
    }
    if (!formData.instructions.trim()) {
      showToast("Skill instructions are required", "error");
      return;
    }

    try {
      setIsModalLoading(true);
      if (editingSkill) {
        await api.put(`/skills/${editingSkill.id}`, {
          name: formData.name.trim(),
          description: formData.description.trim(),
          instructions: formData.instructions,
          is_active: formData.is_active
        });
        showToast("Skill updated successfully");
      } else {
        await api.post("/skills", {
          name: formData.name.trim(),
          description: formData.description.trim(),
          instructions: formData.instructions,
          is_active: formData.is_active
        });
        showToast("Skill created successfully");
      }
      setIsModalOpen(false);
      fetchSkills();
    } catch (error) {
      console.error("Error saving skill:", error);
      const detail = error?.response?.detail || error?.message || "Failed to save skill";
      showToast(detail, "error");
    } finally {
      setIsModalLoading(false);
    }
  };

  // Delete Skill
  const handleDelete = async () => {
    if (!skillToDelete) return;
    setIsDeleting(true);
    try {
      await api.delete(`/skills/${skillToDelete.id}`);
      showToast("Skill deleted successfully");
      setIsDeleteModalOpen(false);
      setSkillToDelete(null);
      fetchSkills();
    } catch (error) {
      console.error("Error deleting skill:", error);
      showToast("Failed to delete skill", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  // Toggle Skill Active State
  const toggleSkillActive = async (skill) => {
    const newStatus = !skill.is_active;
    try {
      // Optimistic update
      setSkills((prev) =>
        prev.map((s) => (s.id === skill.id ? { ...s, is_active: newStatus } : s))
      );

      await api.put(`/skills/${skill.id}`, { is_active: newStatus });
      showToast(`Skill "${skill.name}" ${newStatus ? "activated" : "deactivated"}`);
    } catch (error) {
      console.error("Error toggling skill active state:", error);
      showToast("Failed to update skill status", "error");
      fetchSkills();
    }
  };

  // Handle File Upload (.md)
  const handleFileUpload = async (file) => {
    if (!file || !file.name.endsWith(".md")) {
      showToast("Please upload a valid markdown (.md) file", "error");
      return;
    }

    setIsUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append("file", file);

      await api.request("/skills/upload", {
        method: "POST",
        body: uploadData
      });

      showToast("Skill .md file uploaded successfully");
      await fetchSkills();
    } catch (err) {
      console.error("Error uploading skill file:", err);
      showToast(err.message || "Failed to upload skill file", "error");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Copy helper
  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setIsCopied(true);
    showToast(`${label} copied to clipboard`);
    setTimeout(() => setIsCopied(false), 2000);
  };

  // Filtered skills
  const filteredSkills = useMemo(() => {
    if (!skills) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return skills;
    return skills.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        String(s.id)?.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q)
    );
  }, [skills, searchQuery]);

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
        {/* Hidden File Input */}
        <input
          type="file"
          ref={fileInputRef}
          accept=".md"
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              handleFileUpload(e.target.files[0]);
            }
          }}
        />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Agent Skills</h1>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Equip your AI agents with domain skills, procedural knowledge, and specialized capabilities.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              onClick={() => setIsSampleModalOpen(true)}
              title="See Sample SKILL.md template"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
            >
              <Eye size={14} />
              <span>Sample</span>
            </button>

            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-800 rounded-lg text-xs font-semibold transition-colors shadow-2xs cursor-pointer disabled:opacity-50"
            >
              {isUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              <span>{isUploading ? "Uploading..." : "Upload .md"}</span>
            </button>

            <button
              onClick={() => handleOpenModal()}
              className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-all font-semibold text-xs shadow-xs active:scale-[0.98] cursor-pointer"
            >
              <Plus size={14} strokeWidth={2.5} />
              <span>Create Skill</span>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="pt-5 pb-4">
          <div className="relative w-full sm:max-w-md">
            <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-zinc-400">
              <Search size={14} strokeWidth={2.5} />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by skill name, ID, or description..."
              className="w-full text-xs border border-zinc-200 rounded-lg pl-9 pr-3 py-2 focus:border-zinc-900 focus:outline-none bg-white transition-colors text-zinc-800 font-medium placeholder-zinc-400 shadow-2xs"
            />
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-xl border border-zinc-200 shadow-2xs overflow-hidden">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 size={24} className="animate-spin text-zinc-400" />
              <p className="text-xs font-semibold text-zinc-500">Loading agent skills...</p>
            </div>
          ) : filteredSkills.length === 0 ? (
            <div className="p-12 text-center">
              <h3 className="text-sm font-semibold text-zinc-900 mb-1">
                {searchQuery ? "No matching skills found" : "No Skills Registered Yet"}
              </h3>
              <p className="text-xs text-zinc-500 max-w-sm mx-auto mb-4">
                {searchQuery
                  ? `No skills match "${searchQuery}". Clear your search query.`
                  : "Equip your AI agent with specialized domain knowledge by creating a skill or uploading a SKILL.md file."}
              </p>
              {!searchQuery && (
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-zinc-200 text-zinc-800 rounded-lg hover:bg-zinc-50 transition-all font-semibold text-xs shadow-2xs cursor-pointer"
                  >
                    <Upload size={13} strokeWidth={2.5} />
                    <span>Upload .md</span>
                  </button>
                  <button
                    onClick={() => handleOpenModal()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-all font-semibold text-xs shadow-xs cursor-pointer"
                  >
                    <Plus size={13} strokeWidth={2.5} />
                    <span>Create Skill</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Mobile Card List (visible on screens < md) */}
              <div className="block md:hidden divide-y divide-zinc-100">
                {filteredSkills.map((skill) => (
                  <div key={skill.id} className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-zinc-900 text-sm">{skill.name}</h4>
                        <button
                          onClick={() => handleOpenModal(skill)}
                          className="text-zinc-400 hover:text-zinc-900 p-1 rounded hover:bg-zinc-100 cursor-pointer"
                          title="Edit Skill"
                        >
                          <Edit2 size={12} />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleSkillActive(skill)}
                        title={skill.is_active ? "Deactivate Skill" : "Activate Skill"}
                        className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 cursor-pointer ${
                          skill.is_active ? "bg-zinc-900" : "bg-zinc-200"
                        }`}
                      >
                        <div
                          className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform shadow-xs ${
                            skill.is_active ? "left-4" : "left-0.5"
                          }`}
                        />
                      </button>
                    </div>

                    {skill.description && (
                      <p className="text-xs text-zinc-500 leading-relaxed line-clamp-2">
                        {skill.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between text-[11px] bg-zinc-50 p-2 rounded-lg border border-zinc-100">
                      <span className="text-zinc-400 font-medium">Skill ID:</span>
                      <div className="flex items-center gap-1.5 font-mono text-zinc-700">
                        <span>#{skill.id}</span>
                        <button
                          onClick={() => handleCopy(String(skill.id), "Skill ID")}
                          className="p-1 text-zinc-400 hover:text-zinc-900 cursor-pointer"
                        >
                          <Copy size={11} />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
                      <button
                        onClick={() => handleOpenCodeModal(skill)}
                        className="px-2.5 py-1.5 text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-lg text-xs font-semibold flex items-center gap-1 cursor-pointer"
                      >
                        <Code size={13} />
                        <span>Instructions</span>
                      </button>
                      <button
                        onClick={() => handleOpenDeleteModal(skill)}
                        className="px-2.5 py-1.5 text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg text-xs font-semibold flex items-center gap-1 cursor-pointer"
                      >
                        <Trash2 size={13} />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table (visible on screens >= md) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50/50">
                      <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Skill Name</th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Skill ID</th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Description</th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-center">Format</th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-center">Status</th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-right pr-6">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100 text-xs">
                    {filteredSkills.map((skill) => (
                      <tr key={skill.id} className="hover:bg-zinc-50/70 transition-colors group">
                        {/* Name */}
                        <td className="px-4 py-3.5 font-semibold text-zinc-900 align-middle">
                          <div className="flex items-center gap-2">
                            <span className="truncate max-w-[200px]">{skill.name}</span>
                            <button
                              onClick={() => handleOpenModal(skill)}
                              className="text-zinc-400 hover:text-zinc-900 transition-colors p-1 rounded hover:bg-zinc-100 cursor-pointer"
                              title="Edit Skill"
                            >
                              <Edit2 size={12} />
                            </button>
                          </div>
                        </td>

                        {/* ID */}
                        <td className="px-4 py-3.5 align-middle">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-[11px] text-zinc-600 bg-zinc-100/80 px-2 py-0.5 rounded border border-zinc-200/60">
                              #{skill.id}
                            </span>
                            <button
                              onClick={() => handleCopy(String(skill.id), "Skill ID")}
                              className="p-1 text-zinc-400 hover:text-zinc-900 transition-colors rounded hover:bg-zinc-100 cursor-pointer"
                              title="Copy Skill ID"
                            >
                              <Copy size={12} />
                            </button>
                          </div>
                        </td>

                        {/* Description */}
                        <td className="px-4 py-3.5 text-zinc-500 max-w-[280px] truncate align-middle">
                          {skill.description || "—"}
                        </td>

                        {/* Format Badge */}
                        <td className="px-4 py-3.5 align-middle text-center">
                          <span className="font-mono text-[10px] font-bold text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded border border-zinc-200">
                            SKILL.md
                          </span>
                        </td>

                        {/* Status Toggle */}
                        <td className="px-4 py-3.5 align-middle text-center">
                          <div className="flex items-center justify-center">
                            <button
                              type="button"
                              onClick={() => toggleSkillActive(skill)}
                              title={skill.is_active ? "Deactivate Skill" : "Activate Skill"}
                              className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 cursor-pointer focus:outline-none ${
                                skill.is_active ? "bg-zinc-900" : "bg-zinc-200 hover:bg-zinc-300"
                              }`}
                            >
                              <div
                                className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform shadow-xs ${
                                  skill.is_active ? "left-4" : "left-0.5"
                                }`}
                              />
                            </button>
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3.5 align-middle text-right pr-6">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => handleOpenCodeModal(skill)}
                              className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors border border-transparent hover:border-zinc-200 cursor-pointer"
                              title="View Skill Instructions"
                            >
                              <Code size={14} />
                            </button>
                            <button
                              onClick={() => handleOpenDeleteModal(skill)}
                              className="p-1.5 text-zinc-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                              title="Delete Skill"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create / Edit Skill Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-xl border border-zinc-200">
            <div className="px-6 py-4 border-b border-zinc-100 flex justify-between items-center">
              <div>
                <h3 className="text-sm font-bold text-zinc-900">
                  {editingSkill ? "Edit Agent Skill" : "Create Agent Skill"}
                </h3>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  Define skill title, description, and Markdown instructional guidelines
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1 text-zinc-400 hover:text-zinc-900 rounded-lg transition-colors cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              {/* Skill Name */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-zinc-700">Skill Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg focus:outline-none focus:border-zinc-900 text-zinc-900 text-xs font-mono transition-colors"
                  placeholder="e.g. customer_onboarding_skill"
                />
              </div>

              {/* Description */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-zinc-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg focus:outline-none focus:border-zinc-900 text-zinc-900 text-xs transition-colors resize-none"
                  placeholder="Short explanation of what this skill enables the agent to do..."
                  rows={2}
                />
              </div>

              {/* Instructions */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-semibold text-zinc-700">
                    Instructions (SKILL.md Body) *
                  </label>
                  <span className="text-[10px] text-zinc-400 font-mono">Markdown supported</span>
                </div>
                <textarea
                  required
                  value={formData.instructions}
                  onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                  className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-lg focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono text-xs transition-colors resize-y min-h-[160px]"
                  placeholder={`# Skill Instructions\n\nProvide step-by-step procedures, rules, or references here.`}
                  rows={7}
                />
              </div>

              {/* Is Active Toggle */}
              <div className="flex items-center justify-between pt-1">
                <div>
                  <span className="text-xs font-semibold text-zinc-900">Active State</span>
                  <p className="text-[11px] text-zinc-500">Allow agent to access this skill module</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 cursor-pointer ${
                    formData.is_active ? "bg-zinc-900" : "bg-zinc-200"
                  }`}
                >
                  <div
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out ${
                      formData.is_active ? "translate-x-4" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-zinc-600 hover:text-zinc-900 text-xs font-semibold rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isModalLoading}
                  className="px-4 py-2 bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg text-xs font-semibold shadow-xs flex items-center gap-1.5 transition-all active:scale-[0.98] disabled:opacity-50 cursor-pointer"
                >
                  {isModalLoading && <Loader2 size={13} className="animate-spin" />}
                  <span>{editingSkill ? "Save Changes" : "Create Skill"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Code & Instructions Viewer Modal */}
      {isCodeModalOpen && (
        <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden shadow-xl border border-zinc-200 flex flex-col max-h-[85vh]">
            <div className="px-6 py-4 border-b border-zinc-100 flex justify-between items-center bg-zinc-50/50">
              <div className="flex items-center gap-3">
                <FileCode2 className="text-zinc-700 shrink-0" size={18} />
                <div>
                  <h3 className="text-sm font-bold text-zinc-900">
                    {selectedSkillDetail?.name || "Skill Instructions"}
                  </h3>
                  <div className="flex items-center gap-2 text-[11px] text-zinc-500 mt-0.5">
                    <span className="font-mono">ID: #{selectedSkillDetail?.id}</span>
                    <span>•</span>
                    <span className={selectedSkillDetail?.is_active ? "text-emerald-600 font-semibold" : "text-zinc-400"}>
                      {selectedSkillDetail?.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const text = codeModalTab === "instructions"
                      ? (selectedSkillDetail?.instructions || "")
                      : JSON.stringify(selectedSkillDetail, null, 2);
                    handleCopy(text, codeModalTab === "instructions" ? "Instructions" : "JSON schema");
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-zinc-700 bg-white hover:bg-zinc-100 rounded-lg border border-zinc-200 transition-colors shadow-2xs cursor-pointer"
                >
                  {isCopied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                  <span>{isCopied ? "Copied" : "Copy"}</span>
                </button>
                <button
                  onClick={() => setIsCodeModalOpen(false)}
                  className="p-1.5 text-zinc-400 hover:text-zinc-900 rounded-lg transition-colors hover:bg-zinc-100 cursor-pointer"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="px-6 pt-3 pb-2 flex items-center gap-2 border-b border-zinc-100 bg-white">
              <button
                type="button"
                onClick={() => setCodeModalTab("instructions")}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                  codeModalTab === "instructions"
                    ? "bg-zinc-900 text-white shadow-xs"
                    : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
                }`}
              >
                Skill Instructions
              </button>
              <button
                type="button"
                onClick={() => setCodeModalTab("json")}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                  codeModalTab === "json"
                    ? "bg-zinc-900 text-white shadow-xs"
                    : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
                }`}
              >
                Full JSON Schema
              </button>
            </div>

            {/* Content */}
            <div className="p-6 flex-1 overflow-y-auto bg-zinc-950 text-zinc-100 font-mono text-xs leading-relaxed">
              {isCodeLoading ? (
                <div className="flex items-center justify-center py-12 gap-2 text-zinc-400">
                  <Loader2 size={18} className="animate-spin" />
                  <span>Loading skill content...</span>
                </div>
              ) : codeModalTab === "instructions" ? (
                <pre className="whitespace-pre-wrap font-mono">
                  {selectedSkillDetail?.instructions || "// No instructions defined."}
                </pre>
              ) : (
                <pre className="whitespace-pre-wrap font-mono text-emerald-400">
                  {JSON.stringify(selectedSkillDetail, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sample Template Modal */}
      {isSampleModalOpen && (
        <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden shadow-xl border border-zinc-200 flex flex-col max-h-[85vh]">
            <div className="px-6 py-4 border-b border-zinc-100 flex justify-between items-center bg-zinc-50/50">
              <div className="flex items-center gap-3">
                <FileText className="text-zinc-700 shrink-0" size={18} />
                <div>
                  <h3 className="text-sm font-bold text-zinc-900">Sample SKILL.md Template</h3>
                  <p className="text-[11px] text-zinc-500 mt-0.5">
                    Recommended markdown frontmatter & instruction structure
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleCopy(SAMPLE_SKILL_MD, "Sample SKILL.md")}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-zinc-700 bg-white hover:bg-zinc-100 rounded-lg border border-zinc-200 transition-colors shadow-2xs cursor-pointer"
                >
                  <Copy size={13} />
                  <span>Copy Template</span>
                </button>
                <button
                  onClick={() => setIsSampleModalOpen(false)}
                  className="p-1.5 text-zinc-400 hover:text-zinc-900 rounded-lg transition-colors hover:bg-zinc-100 cursor-pointer"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="p-6 flex-1 overflow-y-auto bg-zinc-950 text-emerald-400 font-mono text-xs leading-relaxed">
              <pre className="whitespace-pre-wrap font-mono">
                {SAMPLE_SKILL_MD}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSkillToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Agent Skill"
        message={`Are you sure you want to permanently delete skill "${skillToDelete?.name}"? This action cannot be reversed.`}
        loading={isDeleting}
      />
    </div>
  );
};

export default Skills;
